import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PortfolioIcon } from '@/components/ui/PortfolioIcon'

// Ten equal six-second slots: the portrait occupies 10% of an automatic cycle.
const SLIDE_DURATION_MS = 6000
const scenes = [
  '/images/portfolio/workbench.webp',
  '/images/articles/gateway.webp',
  '/images/carousel/rk3568.webp',
  '/images/carousel/vending.webp',
  '/images/carousel/fire.webp',
  '/images/articles/operations.webp',
  '/images/articles/dextro-cloud.webp',
  '/images/carousel/bench.webp',
  '/images/portfolio/notebook.webp',
  '/images/carousel/controller.webp',
] as const

type SlideCopy = {
  label: string
  title: string
  description: string
  alt: string
}

export function HeroCarousel() {
  const { t } = useTranslation()
  const slides = t('portfolio.hero.carousel.slides', {
    returnObjects: true,
  }) as SlideCopy[]
  const [active, setActive] = useState(0)
  const [paused, setPaused] = useState(true)
  const [visible, setVisible] = useState(true)
  const slide = slides[active] ?? slides[0]

  useEffect(() => {
    const onVisibilityChange = () =>
      setVisible(document.visibilityState === 'visible')
    document.addEventListener('visibilitychange', onVisibilityChange)
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPaused(media.matches)
    onVisibilityChange()
    const onMotionChange = (event: MediaQueryListEvent) =>
      setPaused(event.matches)
    media.addEventListener('change', onMotionChange)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      media.removeEventListener('change', onMotionChange)
    }
  }, [])

  useEffect(() => {
    if (paused || !visible) return
    const timer = window.setTimeout(
      () => setActive((current) => (current + 1) % scenes.length),
      SLIDE_DURATION_MS,
    )
    return () => window.clearTimeout(timer)
  }, [active, paused, visible])

  useEffect(() => {
    const nextImage = new Image()
    nextImage.src = scenes[(active + 1) % scenes.length] ?? scenes[0]
  }, [active])

  if (!slide) return null

  return (
    <div
      className={active === 4 ? 'hero-visual hero-fire-scene' : 'hero-visual'}
      role="region"
      aria-label={t('portfolio.hero.carousel.label')}
    >
      <img
        key={active}
        className={`hero-carousel-image${active === 2 ? ' carousel-rk3568' : active === 3 ? ' carousel-vending' : ''}`}
        src={scenes[active]}
        alt={slide.alt}
        width="1536"
        height="1024"
        fetchPriority={active === 0 ? 'high' : 'auto'}
      />
      <div className="hero-slide-copy" aria-live="off">
        <p className="carousel-label">{slide.label}</p>
        <h3>{slide.title}</h3>
        <p>{slide.description}</p>
      </div>
      <div className="hero-carousel-controls">
        <div className="carousel-pagination">
          {scenes.map((_, index) => (
            <button
              key={index}
              type="button"
              className={
                index === active ? 'carousel-dot is-selected' : 'carousel-dot'
              }
              aria-label={`${t('portfolio.hero.carousel.goTo')} ${index + 1}: ${slides[index]?.label ?? ''}`}
              aria-pressed={index === active}
              onClick={() => setActive(index)}
            >
              <span />
            </button>
          ))}
        </div>
        <div className="carousel-navigation">
          <button
            type="button"
            aria-label={t('portfolio.hero.carousel.previous')}
            onClick={() =>
              setActive((active + scenes.length - 1) % scenes.length)
            }
          >
            <PortfolioIcon name="arrow" className="carousel-previous" />
          </button>
          <button
            type="button"
            aria-label={t(
              `portfolio.hero.carousel.${paused ? 'play' : 'pause'}`,
            )}
            aria-pressed={paused}
            onClick={() => setPaused(!paused)}
          >
            {paused ? (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="m8 5 11 7-11 7Z" fill="currentColor" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M8 5v14M16 5v14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                />
              </svg>
            )}
          </button>
          <button
            type="button"
            aria-label={t('portfolio.hero.carousel.next')}
            onClick={() => setActive((active + 1) % scenes.length)}
          >
            <PortfolioIcon name="arrow" />
          </button>
        </div>
      </div>
    </div>
  )
}
